import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Clock, Search, CheckCircle2, ArrowLeft, Printer } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { usePersistedState } from "@/hooks/usePersistedState";

function formatEUR(v) {
  return Number(v || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
}

function formatDateIT(iso) {
  if (!iso) return "—";
  try {
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
  } catch { return iso; }
}

export default function Sospesi() {
  const { user, branding } = useAuth();
  const isAgente = user?.role === "agente" || user?.role === "interno" || user?.role === "consultazione";
  const hasPlurimandatari = !!user?.agency?.plurimandatari_abilitato;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = usePersistedState("sospesi:search", "");
  // Filtro collaboratori: mode = "include" (solo i selezionati) oppure "exclude"
  // (tutti tranne i selezionati). Set di nomi (label originali, sensibili al case
  // per la label mostrata; il match viene fatto case-insensitive dal backend).
  const [collabMode, setCollabMode] = usePersistedState("sospesi:collabMode", "include");
  const [collabArr, setCollabArr] = usePersistedState("sospesi:collab", []);
  const collabSet = useMemo(() => new Set(collabArr), [collabArr]);
  const setCollabSet = useCallback((s) => setCollabArr(Array.from(s)), [setCollabArr]);
  const [compagniaFilter, setCompagniaFilter] = usePersistedState("sospesi:compagnia", "all");
  // Range date su data_foglio_cassa. "" = non impostato. Se to_date vuoto ma
  // from_date presente, il backend usa oggi come limite superiore.
  const [dateFrom, setDateFrom] = usePersistedState("sospesi:from", "");
  const [dateTo, setDateTo] = usePersistedState("sospesi:to", "");
  const [selected, setSelected] = useState({});
  const [recDialog, setRecDialog] = useState({ open: false, ids: [], metodo: "Recupero contante", note: "", submitting: false });

  // Costruisce la query string coerente con backend. `withCollab=true` include
  // i filtri include/exclude collab (usato per il PDF); false per il fetch della
  // lista, così `collabOptions` resta popolato con tutti i nomi visibili e
  // possiamo cambiare selezione senza ri-fetchare.
  const buildQueryParams = useCallback((withCollab = false) => {
    const params = new URLSearchParams();
    if (dateFrom) params.set("from_date", dateFrom);
    if (dateTo) params.set("to_date", dateTo);
    if (withCollab && isAgente && collabSet.size > 0) {
      const key = collabMode === "include" ? "collab_include" : "collab_exclude";
      for (const name of collabSet) params.append(key, name);
    }
    return params;
  }, [dateFrom, dateTo, collabMode, collabSet, isAgente]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch senza filtro collab: la lista completa arriva, il filtro
      // include/exclude è applicato lato client (istantaneo, no reload).
      const qs = buildQueryParams(false).toString();
      const { data } = await api.get(`/sospesi${qs ? `?${qs}` : ""}`);
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [buildQueryParams]);

  useEffect(() => { load(); }, [load]);

  // Pool persistente dei collaboratori conosciuti. Accumula i nomi visti in
  // tutte le fetch (mai svuota): così cambiando range date i chip disponibili
  // NON si restringono e l'utente può sempre modificare il filtro collab
  // combinandolo con qualsiasi range date.
  const [knownCollabs, setKnownCollabs] = useState(() => new Set());
  // Pool persistente delle compagnie note — stessa logica.
  const [knownCompagnie, setKnownCompagnie] = useState(() => new Map());

  // Pre-load al mount: fetch SENZA filtri per popolare i pool con tutto l'universo
  // di collab/compagnie visibili all'utente. In questo modo, anche se le date
  // successive filtrano fuori tutto, i chip e il dropdown Compagnia restano
  // disponibili per essere modificati.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/sospesi?include_recuperati=true");
        if (cancelled) return;
        const collabs = new Set();
        const companies = new Map();
        for (const it of (Array.isArray(data) ? data : [])) {
          const c = (it._collab_display || "").trim();
          if (c) collabs.add(c);
          const cid = it.compagnia_id;
          const cnome = (it._compagnia_nome || "").trim();
          if (cid && cnome && !companies.has(cid)) companies.set(cid, cnome);
        }
        setKnownCollabs((prev) => {
          const next = new Set(prev);
          for (const c of collabs) next.add(c);
          return next;
        });
        setKnownCompagnie((prev) => {
          const next = new Map(prev);
          for (const [k, v] of companies) if (!next.has(k)) next.set(k, v);
          return next;
        });
      } catch (_e) {
        // Silenzioso: se questa fetch fallisce il pool resta vuoto ma i filtri
        // di data continuano a funzionare (i chip appariranno appena si popolano
        // da una load con dati).
      }
    })();
    return () => { cancelled = true; };
  }, []); // mount-only

  // Aggiornamento incrementale dei pool ad ogni cambio di `items`.
  useEffect(() => {
    if (!items || items.length === 0) return;
    setKnownCollabs((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const it of items) {
        const label = (it._collab_display || "").trim();
        if (label && !next.has(label)) { next.add(label); changed = true; }
      }
      return changed ? next : prev;
    });
    setKnownCompagnie((prev) => {
      let changed = false;
      const next = new Map(prev);
      for (const it of items) {
        const cid = it.compagnia_id;
        const cnome = (it._compagnia_nome || "").trim();
        if (cid && cnome && !next.has(cid)) { next.set(cid, cnome); changed = true; }
      }
      return changed ? next : prev;
    });
  }, [items]);

  const collabOptions = useMemo(() => {
    // Chip mostrati = tutti i collab conosciuti + eventuali collab attualmente
    // selezionati (per casi limite di ordine di eventi). Case-insensitive dedupe.
    const seen = new Map();
    for (const label of knownCollabs) {
      const k = label.toLowerCase();
      if (!seen.has(k)) seen.set(k, label);
    }
    for (const label of collabSet) {
      const k = label.toLowerCase();
      if (!seen.has(k)) seen.set(k, label);
    }
    return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1], "it"));
  }, [knownCollabs, collabSet]);

  const compagniaOptions = useMemo(() => {
    // Deriva dal pool persistente (non da `items`): così il dropdown resta
    // disponibile anche quando le date filtrano fuori tutto.
    return Array.from(knownCompagnie.entries())
      .sort((a, b) => a[1].localeCompare(b[1], "it"));
  }, [knownCompagnie]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    // Filtro collaboratori lato client: matching case-insensitive.
    const collabLower = new Set(Array.from(collabSet).map((n) => n.toLowerCase()));
    return items.filter((it) => {
      if (isAgente && collabLower.size > 0) {
        const label = (it._collab_display || "").trim().toLowerCase();
        const inSet = collabLower.has(label);
        if (collabMode === "include" && !inSet) return false;
        if (collabMode === "exclude" && inSet) return false;
      }
      if (compagniaFilter !== "all") {
        if ((it.compagnia_id || "") !== compagniaFilter) return false;
      }
      if (q) {
        const hay = `${it.contraente || ""} ${it.numero_polizza || ""} ${it._collab_display || ""} ${it._compagnia_nome || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, search, compagniaFilter, collabMode, collabSet, isAgente]);

  const totale = filtered.reduce((acc, it) => acc + Number(it.importo || 0), 0);

  // "Filtri attivi" = qualsiasi filtro che restringe la lista completa.
  // Usato per mostrare "Indietro" (svuota filtri) al posto di "Dashboard".
  const hasActiveFilters = (
    !!dateFrom || !!dateTo || collabSet.size > 0 ||
    search.trim() !== "" || compagniaFilter !== "all"
  );

  const clearAllFilters = () => {
    setDateFrom("");
    setDateTo("");
    setCollabSet(new Set());
    setCollabMode("include");
    setSearch("");
    setCompagniaFilter("all");
  };

  const toggleOne = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const toggleAll = (v) => {
    const next = {};
    if (v) for (const it of filtered) next[it.id] = true;
    setSelected(next);
  };
  const selectedIds = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);
  const allSelected = filtered.length > 0 && filtered.every((it) => selected[it.id]);

  const openRecupera = (ids) => {
    setRecDialog({ open: true, ids, metodo: "Recupero contante", note: "", submitting: false });
  };
  const closeRecupera = () => setRecDialog((r) => ({ ...r, open: false }));

  const [printing, setPrinting] = useState(false);
  const printPdf = async () => {
    setPrinting(true);
    try {
      const params = buildQueryParams(true);
      const qs = params.toString();
      const url = `/sospesi/export.pdf${qs ? `?${qs}` : ""}`;
      const r = await api.get(url, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(
        new Blob([r.data], { type: "application/pdf" }),
      );
      const win = window.open(blobUrl, "_blank");
      if (!win) {
        // Popup blocked → forziamo il download
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = `sospesi_${new Date().toISOString().slice(0, 10)}.pdf`;
        a.click();
      }
      // Cleanup dopo un po'
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPrinting(false);
    }
  };

  const confermaRecupera = async () => {
    setRecDialog((r) => ({ ...r, submitting: true }));
    let okCount = 0;
    let firstError = null;
    for (const id of recDialog.ids) {
      try {
        await api.post(`/sospesi/${id}/recupera`, {
          metodo_recupero: recDialog.metodo,
          note: recDialog.note || null,
        });
        okCount++;
      } catch (e) {
        firstError = firstError || formatApiError(e);
      }
    }
    setRecDialog((r) => ({ ...r, submitting: false, open: false }));
    setSelected({});
    if (okCount > 0) {
      toast.success(`Recuperati ${okCount} sospes${okCount === 1 ? "o" : "i"}. Aggiunti al foglio cassa del giorno.`);
    }
    if (firstError) {
      toast.error(firstError);
    }
    await load();
  };

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-8 space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {hasActiveFilters ? (
            <Button
              variant="outline"
              size="sm"
              onClick={clearAllFilters}
              data-testid="sospesi-back"
              title="Torna alla lista completa dei sospesi"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Indietro
            </Button>
          ) : isAgente ? (
            <Link to="/admin">
              <Button variant="outline" size="sm" data-testid="sospesi-back">
                <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
              </Button>
            </Link>
          ) : (
            <Link to="/foglio-cassa">
              <Button variant="outline" size="sm" data-testid="sospesi-back">
                <ArrowLeft className="w-4 h-4 mr-1" /> Foglio cassa
              </Button>
            </Link>
          )}
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" /> Sospesi di agenzia
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">
              {isAgente ? "Sospesi di agenzia" : "I miei sospesi"}
            </h1>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Totale sospesi visibili</div>
          <div className="font-heading text-xl md:text-2xl font-bold text-slate-900 font-mono-num" data-testid="sospesi-totale">
            {formatEUR(totale)}
          </div>
          <div className="text-[11px] text-slate-500">{filtered.length} {filtered.length === 1 ? "polizza" : "polizze"}</div>
        </div>
      </div>

      <Card className="p-4 border-slate-200">
        <div className="flex flex-col md:flex-row md:items-end gap-3">
          <div className="flex-1">
            <Label className="text-xs uppercase tracking-wider text-slate-500">Cerca per contraente o n. polizza</Label>
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" aria-hidden="true" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Es. Rossi o AB1234"
                className="pl-8 h-9"
                data-testid="sospesi-search"
              />
            </div>
          </div>
          <div className="w-full md:w-40">
            <Label htmlFor="sospesi-date-from" className="text-xs uppercase tracking-wider text-slate-500">Dal</Label>
            <Input
              id="sospesi-date-from"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="h-9"
              data-testid="sospesi-date-from"
            />
          </div>
          <div className="w-full md:w-40">
            <Label htmlFor="sospesi-date-to" className="text-xs uppercase tracking-wider text-slate-500">Al (vuoto = oggi)</Label>
            <Input
              id="sospesi-date-to"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="h-9"
              data-testid="sospesi-date-to"
            />
          </div>
          {hasPlurimandatari && compagniaOptions.length > 0 && (
            <div className="w-full md:w-56">
              <Label className="text-xs uppercase tracking-wider text-slate-500">Compagnia</Label>
              <Select value={compagniaFilter} onValueChange={setCompagniaFilter}>
                <SelectTrigger className="h-9" data-testid="sospesi-compagnia-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte</SelectItem>
                  {compagniaOptions.map(([id, label]) => (
                    <SelectItem key={id} value={id}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {selectedIds.length > 0 && (
            <Button
              onClick={() => openRecupera(selectedIds)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="sospesi-bulk-recupera"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Recupera selezionati ({selectedIds.length})
            </Button>
          )}
          <Button
            variant="outline"
            onClick={printPdf}
            disabled={filtered.length === 0 || printing}
            data-testid="sospesi-print-btn"
          >
            <Printer className="w-4 h-4 mr-2" />
            {printing ? "Genero PDF…" : "Stampa PDF"}
          </Button>
        </div>
        {isAgente && collabOptions.length > 0 && (
          <div className="mt-4 pt-4 border-t border-slate-200">
            <div className="flex flex-col md:flex-row md:items-center gap-3 mb-2">
              <Label className="text-xs uppercase tracking-wider text-slate-500">
                Filtro collaboratori
              </Label>
              <div className="flex items-center gap-4 text-sm">
                <label className="flex items-center gap-1.5 cursor-pointer" data-testid="sospesi-collab-mode-include">
                  <input
                    type="radio"
                    name="collab-mode"
                    value="include"
                    checked={collabMode === "include"}
                    onChange={() => setCollabMode("include")}
                    className="cursor-pointer"
                  />
                  <span>Solo i selezionati</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer" data-testid="sospesi-collab-mode-exclude">
                  <input
                    type="radio"
                    name="collab-mode"
                    value="exclude"
                    checked={collabMode === "exclude"}
                    onChange={() => setCollabMode("exclude")}
                    className="cursor-pointer"
                  />
                  <span>Tutti tranne i selezionati</span>
                </label>
              </div>
              {collabSet.size > 0 && (
                <button
                  type="button"
                  onClick={() => setCollabSet(new Set())}
                  className="text-xs text-slate-500 hover:text-slate-700 underline underline-offset-2"
                  data-testid="sospesi-collab-clear"
                >
                  Deseleziona tutti
                </button>
              )}
              <div className="text-xs text-slate-500 md:ml-auto">
                {collabSet.size === 0
                  ? "Nessuno selezionato → mostro tutti"
                  : `${collabSet.size} selezionat${collabSet.size === 1 ? "o" : "i"}`}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {collabOptions.map(([key, label]) => {
                const active = collabSet.has(label);
                return (
                  <label
                    key={key}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-sm cursor-pointer transition-colors ${
                      active
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                    }`}
                    data-testid={`sospesi-collab-chip-${key}`}
                  >
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={(e) => {
                        const next = new Set(collabSet);
                        if (e.target.checked) next.add(label);
                        else next.delete(label);
                        setCollabSet(next);
                      }}
                      className="cursor-pointer"
                    />
                    <span>{label}</span>
                  </label>
                );
              })}
            </div>
          </div>
        )}
      </Card>

      <Card className="border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500 text-sm">Caricamento…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <Clock className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <div className="font-heading font-semibold text-slate-700">Nessun sospeso da mostrare</div>
            <div className="text-sm text-slate-500 mt-1">
              {items.length === 0
                ? "Non hai ancora inserito polizze con metodo 'Sospeso'."
                : "Nessun sospeso corrisponde ai filtri."}
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="sospesi-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-3 py-2 w-8">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={toggleAll}
                      data-testid="sospesi-toggle-all"
                      aria-label="Seleziona tutti"
                    />
                  </th>
                  <th className="px-3 py-2 text-left text-xs uppercase tracking-wider text-slate-500 font-semibold">Data</th>
                  {isAgente && <th className="px-3 py-2 text-left text-xs uppercase tracking-wider text-slate-500 font-semibold">Collaboratore</th>}
                  <th className="px-3 py-2 text-left text-xs uppercase tracking-wider text-slate-500 font-semibold">Contraente</th>
                  <th className="px-3 py-2 text-left text-xs uppercase tracking-wider text-slate-500 font-semibold">N. Polizza</th>
                  <th className="px-3 py-2 text-right text-xs uppercase tracking-wider text-slate-500 font-semibold">Importo</th>
                  <th className="px-3 py-2 text-right text-xs uppercase tracking-wider text-slate-500 font-semibold">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((it) => {
                  const collabLabel = (it._collab_display || "").trim() || "—";
                  return (
                    <tr key={it.id} className="hover:bg-slate-50" data-testid={`sospesi-row-${it.id}`}>
                      <td className="px-3 py-2">
                        <Checkbox
                          checked={!!selected[it.id]}
                          onCheckedChange={() => toggleOne(it.id)}
                          data-testid={`sospesi-check-${it.id}`}
                          aria-label="Seleziona"
                        />
                      </td>
                      <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{formatDateIT(it.data_contabile_foglio_cassa)}</td>
                      {isAgente && (
                        <td className="px-3 py-2 text-slate-700">{collabLabel}</td>
                      )}
                      <td className="px-3 py-2 text-slate-900 font-medium">{it.contraente || "—"}</td>
                      <td className="px-3 py-2 text-slate-700 font-mono">{it.numero_polizza || "—"}</td>
                      <td className="px-3 py-2 text-right text-slate-900 font-mono-num font-semibold whitespace-nowrap">{formatEUR(it.importo)}</td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openRecupera([it.id])}
                          data-testid={`sospesi-recupera-${it.id}`}
                        >
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1 text-emerald-600" /> Recupera
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Dialog open={recDialog.open} onOpenChange={(v) => !v && closeRecupera()}>
        <DialogContent className="sm:max-w-md" data-testid="sospesi-recupera-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              Recupera {recDialog.ids.length > 1 ? `${recDialog.ids.length} sospesi` : "sospeso"}
            </DialogTitle>
            <DialogDescription>
              Le polizze selezionate verranno aggiunte al tuo foglio cassa del giorno con il metodo di recupero scelto e rimosse dalla lista sospesi.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Metodo di recupero</Label>
              <Select value={recDialog.metodo} onValueChange={(v) => setRecDialog((r) => ({ ...r, metodo: v }))}>
                <SelectTrigger className="h-9" data-testid="sospesi-recupera-metodo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(() => {
                    const sMetodi = Array.isArray(branding?.sospesi_metodi) ? branding.sospesi_metodi : [];
                    const legacy = ["contante", "assegno"];
                    const active = new Set([...legacy, ...sMetodi]);
                    const LABELS = {
                      contante: "Recupero contante",
                      assegno:  "Recupero assegno",
                      bonifico: "Recupero bonifico",
                      pos:      "Recupero POS",
                      postale:  "Recupero postale",
                    };
                    return ["contante", "assegno", "bonifico", "pos", "postale"]
                      .filter((k) => active.has(k))
                      .map((k) => (
                        <SelectItem key={k} value={LABELS[k]}>{LABELS[k]}</SelectItem>
                      ));
                  })()}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Note (opzionale)</Label>
              <Input
                value={recDialog.note}
                onChange={(e) => setRecDialog((r) => ({ ...r, note: e.target.value }))}
                placeholder="Lascia vuoto per usare la nota automatica"
                className="h-9"
                data-testid="sospesi-recupera-note"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={closeRecupera} disabled={recDialog.submitting} data-testid="sospesi-recupera-annulla">Annulla</Button>
            <Button onClick={confermaRecupera} disabled={recDialog.submitting} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="sospesi-recupera-conferma">
              {recDialog.submitting ? "Registrazione…" : "Conferma recupero"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
